import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal, WritableSignal } from '@angular/core';
import { ENV_CONFIG, EnvironmentConfig, IKeyLongValueModel, PageResponse, PageUtil } from '@libs/vc-core-lib';
import { IAssessmentCriteriaDTO } from '../dto/assessment.criteria.dto';
import { IAssessmentModel } from '../model/assessment.model';
import { IControlModel } from '../model/control.model';
import { IStatusUpdateModel } from '../model/status-update.model';
import { ITaskModel } from '../model/task.model';
import { map, Observable, tap } from 'rxjs';
import { IAssessmentPermissionsHolder } from '../model/assessment-permissions-holder.model';
import { IAssessmentSearchDTO } from '../dto/assessment.search.dto';
import { IAssessmentDashboardResultsModel } from '../model/assessment-dashboard-results.model';
import { IMilestonesPatchRequestModel } from '../model/milestones-patch-request.model';
import { IMilestoneRequestModel } from '../model/milestone-request.model';
import { IAssignedUserPatchRequestModel } from '../model/assigned-user-patch-request.model';
import { IAssessorPatchRequestModel } from '../model/assessor-patch-request.model';
import { IAssessmentDetailPatchRequestModel } from '../model/assessment-detail-patch-request.model';
import { ICommentHolderModel } from '../model/comment-holder.model';
import { ITaskDelegationResults } from '../model/task-delegation-results.model';
import { IAdHocTaskCreateRequestCriteriaDTO } from '../dto/ad-hoc-task-create.criteria.dto';
import { Page } from 'vcp-common-ui-lib';
import { ITaskReportRequestModel } from '../model/task-report-request.model';
import { IReportSectionRequestModel } from '../model/report-section-request.model';
import { INarrowUserModel } from '../model/narrow-user.model';
import { IUserNotificationsConfigPatchRequest } from '../model/user-notifications-config-patch-request.model';
import { IAssessmentTimelineEntryModel } from '../model/assessment-timeline-entry.model';
import { IAssessmentCountInfoModel } from '../model/assessment-count-info.model';
import { IAssessmentPercentCompleteInfoModel } from '../model/assessment-percent-complete-info.model';
import { IStatusReportPhasePatchRequestModel } from '../model/status-report-phase-patch-request.model';
import { IStatusReportSectionPatchRequestModel } from '../model/status-report-section-patch-request.model';
import { StatusReportFormat } from '../enum/status-report-format.enum';
import { IBulkTaskCommentModel } from '../model/bulk-task-comment.model';
import { IAssessmentComponentModel } from '../model/assessment-component.model';
import { ICommentModel } from '../model/comment.model';
import { ICommentReplyModel } from '../model/comment-reply.model';
import { AssessmentTasksReviewsTracking } from '../dto/assessment-tasks-reviews-tracking.dto';
import { TaskReviewRequest } from '../model/task-review.model';
import { ConsumedReviewsBreakdown } from '../model/task-reviews-breakdown.model';
import { IAssessmentTableDataDTO } from '../model/assessment-table.model';

@Injectable({
    providedIn: 'root',
})
export class AssessmentService {
    private readonly _httpClient: HttpClient = inject(HttpClient);
    private readonly _environment: EnvironmentConfig = inject(ENV_CONFIG);
    private readonly _assessmentPath = 'assessment';

    private readonly _cachedDashboardResult: WritableSignal<IAssessmentDashboardResultsModel | null> = signal(null);

    public get(id: number): Observable<IAssessmentModel> {
        return this._httpClient.get<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${id}`
        );
    }

    public getLightweightAssessment(id: number): Observable<IAssessmentModel> {
        return this._httpClient.get<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${id}/lightweight`
        );
    }

    public create(assessment: IAssessmentModel): Observable<IAssessmentModel> {
        return this._httpClient.post<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}`,
            assessment
        );
    }

    public delete(id: number): Observable<void> {
        return this._httpClient.delete<void>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${id}`
        );
    }

    public pageSearch(searchDTO: IAssessmentSearchDTO): Observable<Page<IAssessmentTableDataDTO>> {
        return this._httpClient
            .post<
                PageResponse<IAssessmentTableDataDTO>
            >(`${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/page-search`, searchDTO)
            .pipe(map((page) => PageUtil.convertToPage(page)));
    }

    public search(searchDTO: IAssessmentCriteriaDTO): Observable<IAssessmentTableDataDTO[]> {
        return this._httpClient.post<IAssessmentTableDataDTO[]>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/search`,
            searchDTO
        );
    }

    /**
     * Lists existing assessments for the provided list of assessment ids
     * **/
    public listByIds(assessmentIds: number[]): Observable<IAssessmentModel[]> {
        return this._httpClient.post<Array<IAssessmentModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/list`,
            assessmentIds
        );
    }

    public getOnlyPermissions(id: number): Observable<Array<string>> {
        return this._httpClient.get<Array<string>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${id}/permissions-only`
        );
    }

    public getAssessmentWithPermissions(id: number): Observable<IAssessmentPermissionsHolder> {
        return this._httpClient.get<IAssessmentPermissionsHolder>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${id}/permissions`
        );
    }

    public updateTasks(tasks: Array<ITaskModel>): Observable<Array<ITaskModel>> {
        return this._httpClient.put<Array<ITaskModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/task`,
            tasks
        );
    }

    public updateAssessmentStatus(statusUpdate: IStatusUpdateModel): Observable<IAssessmentModel> {
        return this._httpClient.put<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${statusUpdate.assessmentId}/status`,
            statusUpdate
        );
    }

    public assessmentFileExport(assessmentId: number): Observable<Blob> {
        return this._httpClient.get<Blob>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/reports/assessment-file-export`,
            { observe: 'body', responseType: 'blob' as 'json' }
        );
    }

    public assessmentTaskExport(assessmentId: number): Observable<Blob> {
        return this._httpClient.get<Blob>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/reports/assessment-task-export`,
            { observe: 'body', responseType: 'blob' as 'json' }
        );
    }

    public assessmentControlExport(assessmentId: number): Observable<Blob> {
        return this._httpClient.get<Blob>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/reports/assessment-control-export`,
            { observe: 'body', responseType: 'blob' as 'json' }
        );
    }

    public generateAssessmentReport(
        id: number,
        reportType: string,
        subReportType: string,
        force?: boolean
    ): Observable<string> {
        let params = new HttpParams()
            .set('assessmentId', id)
            .set('reportType', reportType)
            .set('subReportType', subReportType);
        params = force ? params.set('force', force.toString()) : params;
        return this._httpClient.post<string>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/generate-report`,
            null,
            {
                observe: 'body',
                responseType: 'text' as 'json',
                params: params,
            }
        );
    }

    public getAvailableStateTransitions(id: number): Observable<Array<string>> {
        return this._httpClient.get<Array<string>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${id}/state-transitions`
        );
    }

    public patchMilestones(
        assessmentId: number,
        patchRequest: IMilestonesPatchRequestModel
    ): Observable<IAssessmentModel> {
        return this._httpClient.patch<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/milestones`,
            patchRequest
        );
    }

    public createMilestone(
        assessmentId: number,
        milestoneRequest: IMilestoneRequestModel
    ): Observable<IAssessmentModel> {
        return this._httpClient.post<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/milestone`,
            milestoneRequest
        );
    }

    public updateMilestone(
        assessmentId: number,
        milestoneRequest: IMilestoneRequestModel
    ): Observable<IAssessmentModel> {
        return this._httpClient.put<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/milestone/${milestoneRequest.milestone.id}`,
            milestoneRequest
        );
    }

    public deleteMilestone(assessmentId: number, milestoneId: number, version: number): Observable<IAssessmentModel> {
        let params = new HttpParams();
        params = params.append('version', version);
        return this._httpClient.delete<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/milestone/${milestoneId}`,
            { params: params }
        );
    }

    public addAssessmentTasks(assessmentId: number, referenceTaskIds: number[]): Observable<Array<ITaskModel>> {
        return this._httpClient.post<Array<ITaskModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/tasks`,
            referenceTaskIds
        );
    }

    public removeTasks(assessmentId: number, taskIds: number[]): Observable<void> {
        return this._httpClient.delete<void>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/tasks`,
            {
                body: taskIds,
            }
        );
    }

    public addAssessmentControls(assessmentId: number, controls: Array<IControlModel>): Observable<IAssessmentModel> {
        return this._httpClient.post<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/controls`,
            controls
        );
    }

    public removeAssessmentControls(
        assessmentId: number,
        controls: Array<IControlModel>
    ): Observable<IAssessmentModel> {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
            body: controls,
        };
        return this._httpClient.delete<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/controls`,
            options
        );
    }

    public addFrameworkComponents(assessmentId: number, frameworkComponentName: string): Observable<IAssessmentModel> {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };
        return this._httpClient.post<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/framework-component`,
            frameworkComponentName,
            options
        );
    }

    public removeFrameworkComponents(
        assessmentId: number,
        frameworkComponentNames: Array<string>
    ): Observable<IAssessmentModel> {
        return this._httpClient.delete<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/framework-components`,
            {
                body: frameworkComponentNames,
            }
        );
    }

    public getAssessmentComponent(assessmentId: number, componentId: number): Observable<IAssessmentComponentModel> {
        return this._httpClient.get<IAssessmentComponentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/component/${componentId}`
        );
    }

    public getAssessmentComponents(assessmentId: number): Observable<Array<IAssessmentComponentModel>> {
        return this._httpClient.get<Array<IAssessmentComponentModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/components`
        );
    }

    public patchClients(
        assessmentId: number,
        patchRequest: IAssignedUserPatchRequestModel
    ): Observable<IAssessmentModel> {
        return this._httpClient.patch<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/clients`,
            patchRequest
        );
    }

    public patchAssessors(
        assessmentId: number,
        patchRequest: IAssessorPatchRequestModel
    ): Observable<IAssessmentModel> {
        return this._httpClient.patch<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/assessors`,
            patchRequest
        );
    }

    public patchDetails(
        assessmentId: number,
        assessmentDetailPatchRequest: IAssessmentDetailPatchRequestModel
    ): Observable<IAssessmentModel> {
        return this._httpClient.patch<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/details`,
            assessmentDetailPatchRequest
        );
    }

    public generateTaskListExports(taskReportRequest: ITaskReportRequestModel): Observable<Blob> {
        const url: string = `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/reports/task-list-export`;
        return this._httpClient.post<Blob>(url, taskReportRequest, { observe: 'body', responseType: 'blob' as 'json' });
    }

    public shareComment(commentHolder: ICommentHolderModel): Observable<void> {
        return this._httpClient.post<void>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/comment/share`,
            commentHolder
        );
    }

    /** Add a comment to bulk tasks and trigger task status transition accordingly **/
    public addBulkTaskComment(
        assessmentId: number,
        bulkTaskCommentModel: IBulkTaskCommentModel
    ): Observable<Map<string, string>> {
        return this._httpClient
            .post<
                Record<string, string>
            >(`${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/bulk-task-comment`, bulkTaskCommentModel)
            .pipe(map((taskObj: Record<string, string>): Map<string, string> => new Map(Object.entries(taskObj))));
    }

    public getAssessmentDashboardTaskDelegation(assessmentId: number): Observable<ITaskDelegationResults> {
        return this._httpClient.get<ITaskDelegationResults>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/tasks/dashboard/delegation`
        );
    }

    public createAdhocTaskForAssessment(
        assessmentId: number,
        criteria: IAdHocTaskCreateRequestCriteriaDTO
    ): Observable<ITaskModel> {
        return this._httpClient.post<ITaskModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/adhoc-task`,
            criteria
        );
    }

    public getAssignedUsers(assessmentId: number): Observable<Array<INarrowUserModel>> {
        return this._httpClient.get<Array<INarrowUserModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/assigned-users`
        );
    }

    public getClientPotentialUsers(assessmentId: number): Observable<Array<INarrowUserModel>> {
        return this._httpClient.get<Array<INarrowUserModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/client-potential-users`
        );
    }

    /** Status Reports **/

    public createReportSection(
        assessmentId: number,
        reportSectionRequest: IReportSectionRequestModel
    ): Observable<IAssessmentModel> {
        return this._httpClient.post<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/report-section`,
            reportSectionRequest
        );
    }

    public updateReportSection(
        assessmentId: number,
        reportSectionId: string,
        reportSectionRequest: IReportSectionRequestModel
    ): Observable<IAssessmentModel> {
        return this._httpClient.put<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/report-section/${reportSectionId}`,
            reportSectionRequest
        );
    }

    public deleteReportSection(
        assessmentId: number,
        reportSectionId: string,
        version: number
    ): Observable<IAssessmentModel> {
        let params = new HttpParams();
        params = params.append('version', version);
        return this._httpClient.delete<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/report-section/${reportSectionId}`,
            { params: params }
        );
    }

    public patchStatusReportPhase(
        assessmentId: number,
        statusReportPhaseId: string,
        statusReportPhasePatchRequest: IStatusReportPhasePatchRequestModel
    ): Observable<IAssessmentModel> {
        return this._httpClient.patch<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/status-report-phase/${statusReportPhaseId}`,
            statusReportPhasePatchRequest
        );
    }

    public patchStatusReportSection(
        assessmentId: number,
        statusReportSectionId: string,
        statusReportSectionPatchRequest: IStatusReportSectionPatchRequestModel
    ): Observable<IAssessmentModel> {
        return this._httpClient.patch<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/status-report-section/${statusReportSectionId}`,
            statusReportSectionPatchRequest
        );
    }

    public generateAssessmentStatusReportPhasesReport(
        assessmentId: number,
        statusReportFormat: StatusReportFormat
    ): Observable<Blob> {
        return this._httpClient.get<Blob>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/reports/status-report-phases/${statusReportFormat}`,
            { observe: 'body', responseType: 'blob' as 'json' }
        );
    }

    public generateAssessmentStatusReportSectionsReport(
        assessmentId: number,
        statusReportFormat: StatusReportFormat
    ): Observable<Blob> {
        return this._httpClient.get<Blob>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/reports/status-report-sections/${statusReportFormat}`,
            { observe: 'body', responseType: 'blob' as 'json' }
        );
    }

    /** Notifications **/

    public patchUserNotificationsConfig(
        assessmentId: number,
        patchRequest: IUserNotificationsConfigPatchRequest
    ): Observable<IAssessmentModel> {
        return this._httpClient.patch<IAssessmentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/notifications-config`,
            patchRequest
        );
    }

    /** Dashboard **/

    public getTopActiveAssessments(): Observable<IAssessmentModel[]> {
        return this._httpClient.get<IAssessmentModel[]>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/top-active`
        );
    }

    public getSingleAssessmentDashboardResult(id: number): Observable<IAssessmentDashboardResultsModel> {
        return this._httpClient
            .get<IAssessmentDashboardResultsModel>(
                `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${id}/dashboard`
            )
            .pipe(
                tap((result: IAssessmentDashboardResultsModel) => {
                    result.assessment.id = id;
                    this._cachedDashboardResult.set(result);
                })
            );
    }

    public getTopRejectedTaskAssessments(): Observable<Array<IAssessmentCountInfoModel>> {
        return this._httpClient.get<Array<IAssessmentCountInfoModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/top-rejected-tasks`
        );
    }

    public getAssessmentsPercentComplete(): Observable<Array<IAssessmentPercentCompleteInfoModel>> {
        return this._httpClient.get<Array<IAssessmentPercentCompleteInfoModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/percent-complete`
        );
    }

    public getAssessmentsCounts(): Observable<Map<string, IKeyLongValueModel>> {
        return this._httpClient.get<Map<string, IKeyLongValueModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/counts`
        );
    }

    public getAssessmentNamesByIds(ids: number[]): Observable<Record<string, string>> {
        return this._httpClient.post<Record<string, string>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/names`,
            ids
        );
    }

    public getAssessmentsByStatus(): Observable<Map<string, IKeyLongValueModel>> {
        return this._httpClient.get<Map<string, IKeyLongValueModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/count-by-status`
        );
    }

    public getTopAtRiskAssessmentList(): Observable<IAssessmentModel[]> {
        return this._httpClient.get<IAssessmentModel[]>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/top-at-risk`
        );
    }

    public getAssessmentByFramework(): Observable<Map<string, IKeyLongValueModel>> {
        return this._httpClient.get<Map<string, IKeyLongValueModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/count-by-framework`
        );
    }

    public getBasicAssessmentTimelineData(
        spanType: string,
        spanLength: number,
        anchorDate?: Date
    ): Observable<Map<string, IAssessmentTimelineEntryModel>> {
        let params = new HttpParams();
        params = params.append('spanType', spanType).append('spanLength', spanLength.toString());
        if (anchorDate) {
            params = params.append('anchorDate', anchorDate.toString());
        }
        return this._httpClient.get<Map<string, IAssessmentTimelineEntryModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/basic-timeline`,
            {
                params: params,
            }
        );
    }

    public addComment(assessmentId: number, comment: ICommentModel): Observable<ICommentModel> {
        return this._httpClient.post<ICommentModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/comment`,
            comment
        );
    }

    public addReply(
        assessmentId: number,
        commentReply: ICommentReplyModel,
        taskId?: number
    ): Observable<ICommentModel> {
        if (taskId) {
            return this._httpClient.post<ICommentModel>(
                `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/task/${taskId}/reply-comment`,
                commentReply
            );
        } else {
            return this._httpClient.post<ICommentModel>(
                `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/reply-comment`,
                commentReply
            );
        }
    }

    /** Task Reviews **/

    public getTaskReviewTracking(assessmentId: number) {
        return this._httpClient.get<AssessmentTasksReviewsTracking>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/task-reviews-tracking`
        );
    }

    public getTaskReviewBreakdown(assessmentId: number) {
        return this._httpClient.get<ConsumedReviewsBreakdown>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/task-reviews-breakdown`
        );
    }

    public modifyTaskReviews(assessmentId: number, request: TaskReviewRequest) {
        return this._httpClient.post<TaskReviewRequest>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/${assessmentId}/task-reviews`,
            request
        );
    }

    public orderComponentsById(assessmentId: number, ids: number[]): Observable<IAssessmentComponentModel[]> {
        return this._httpClient.put<IAssessmentComponentModel[]>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/components/order/${assessmentId}`,
            ids
        );
    }

    public orderTasksById(assessmentId: number, ids: number[]): Observable<ITaskModel[]> {
        return this._httpClient.put<ITaskModel[]>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/${this._assessmentPath}/tasks/order/${assessmentId}`,
            ids
        );
    }
}
